import React, { useState, useEffect, useRef, memo } from 'react';
import { Card, Row, Col, Badge } from 'react-bootstrap';
import { calculateNetCaseWeight, calculateGrossCaseWeight, calculateVolumeCubicFeet } from '../utils/commonUtils';
import api from '../api/axiosConfig';

const StockItemCard = memo(function StockItemCard({ stockItem }) {
    console.log(stockItem)
    const [isWrapped, setIsWrapped] = useState(false);
    const rowRef = useRef(null);

    // Function to get the full image URL
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        // If it's already a full URL, return as is
        if (imagePath.startsWith('http')) return imagePath;
        // Otherwise, prepend the API base URL
        return `${api.defaults.baseURL}${imagePath}`;
    };

    useEffect(() => {
        if (!rowRef.current) return;

        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                // Get the first and second columns
                const columns = entry.target.children;
                if (columns.length < 2) return;

                // Check if columns are wrapped by comparing their y positions
                const firstColRect = columns[0].getBoundingClientRect();
                const lastColRect = columns[columns.length - 1].getBoundingClientRect();
                
                setIsWrapped(firstColRect.top !== lastColRect.top);
            }
        });

        observer.observe(rowRef.current);
        return () => observer.disconnect();
    }, []);

    const netCaseWeight = calculateNetCaseWeight(
        stockItem.item_size,
        stockItem.item_size_units,
        stockItem.case_pack
    );

    const grossCaseWeight = calculateGrossCaseWeight(
        stockItem.item_size,
        stockItem.item_size_units,
        stockItem.case_pack,
        stockItem.package_weight,
        stockItem.package_weight_units
    );

    const getCountryFlag = (countryCode) => {
        if (!countryCode || countryCode.length !== 2) return '';
        const OFFSET = 127397;
        const first = countryCode.charCodeAt(0) + OFFSET;
        const second = countryCode.charCodeAt(1) + OFFSET;
        return String.fromCodePoint(first, second);
    };

    return (
        <Card className="shadow-sm">
            <Card.Body>
                <Card.Title className="justify-content-between align-items-center">
                    <span>{stockItem.description}</span>
                </Card.Title>
                <Card.Text as="div">
                    <Row ref={rowRef}>
                        <Col>
                            <div style={{ position: 'relative' }}>
                                {stockItem.image ? (
                                    <img 
                                        src={getImageUrl(stockItem.image)} 
                                        alt={stockItem.description} 
                                        className="rounded" 
                                        style={{ width: '340px', height: '340px' }}
                                    />
                                ) : (
                                    <div style={{ 
                                        width: '350px', 
                                        height: '350px', 
                                        backgroundColor: 'transparent', 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        alignItems: 'center' 
                                    }}>
                                        <i className="bi bi-image" style={{ fontSize: '50px', color: 'darkgray' }}></i>
                                    </div>
                                )}
                                <div className="d-flex flex-wrap gap-1 p-2" 
                                    style={{ 
                                        position: 'absolute', 
                                        top: 0, 
                                        left: 0, 
                                        right: 0,
                                        background: 'transparent',
                                        borderTopLeftRadius: '0.375rem',
                                        borderTopRightRadius: '0.375rem'
                                    }}>
                                    {stockItem.categories_names.map((category, index) => (
                                        <Badge
                                            key={index}
                                            bg="primary"
                                            className="rounded"
                                        >
                                            {category || 'N/A'}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </Col>
                        {!isWrapped ? (
                            <Col xs="auto" className="p-0" style={{ width: '1px' }}>
                                <div className="border-primary m-0" 
                                    style={{ 
                                        height: '100%', 
                                        borderStyle: 'dotted',
                                        borderWidth: '0px 2px 0px 0px',
                                        borderColor: 'var(--bs-primary)'
                                    }}>
                                </div>
                            </Col>
                        ) : (
                            <Col xs={12} className="p-0 my-3">
                                <div className="border-primary m-0" 
                                    style={{ 
                                        width: '100%', 
                                        borderStyle: 'dotted',
                                        borderWidth: '2px 0px 0px 0px',
                                        borderColor: 'var(--bs-primary)'
                                    }}>
                                </div>
                            </Col>
                        )}
                        <Col>
                            <table className="table table-borderless table-striped" style={{ marginBottom: 0 }}>
                                <tbody style={{ lineHeight: '1', fontSize: '0.85rem' }}>
                                    <tr>
                                        <td className="py-1" >SKU:</td>
                                        <td className="py-1"><strong>{stockItem.sku || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >MANUFACTURER:</td>
                                        <td className="py-1"><strong>{stockItem.manufacturer_name || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >GTIN:</td>
                                        <td className="py-1"><strong>{stockItem.gtin || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >UPC:</td>
                                        <td className="py-1"><strong>{stockItem.upc || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >COUNTRY:</td>
                                        <td className="py-1">
                                            {stockItem.country_of_origin ? (
                                                <strong>
                                                    <span>{getCountryFlag(stockItem.country_of_origin)}{' '}</span>
                                                    <span>{stockItem.country_of_origin}</span>
                                                </strong>
                                            ) : <strong>N/A</strong>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >ITEM SIZE:</td>
                                        <td className="py-1"><strong>{stockItem.item_size ? `${stockItem.item_size} ${stockItem.item_size_units}` : 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >CASE PACK:</td>
                                        <td className="py-1"><strong>{stockItem.case_pack}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >NET CASE WEIGHT:</td>
                                        <td className="py-1"><strong>{netCaseWeight} lbs</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >GROSS CASE WEIGHT:</td>
                                        <td className="py-1"><strong>{grossCaseWeight} lbs</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1">
                                            <i className="bi bi-rulers"></i>
                                        </td>
                                        <td className="py-1">
                                            <strong>
                                                {stockItem.dimensions_l && stockItem.dimensions_w && stockItem.dimensions_h ? (
                                                    `${stockItem.dimensions_l}" × ${stockItem.dimensions_w}" × ${stockItem.dimensions_h}"`
                                                ) : 'N/A'}
                                            </strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >PACKAGE WEIGHT:</td>
                                        <td className="py-1"><strong>{stockItem.package_weight || 'N/A'} {stockItem.package_weight_units}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >TI:</td>
                                        <td className="py-1"><strong>{stockItem.ti || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >HI:</td>
                                        <td className="py-1"><strong>{stockItem.hi || 'N/A'}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="py-1" >VOLUME:</td>
                                        <td className="py-1"><strong>{calculateVolumeCubicFeet(stockItem.dimensions_l, stockItem.dimensions_w, stockItem.dimensions_h) || 'N/A'} cubic ft</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </Col>
                    </Row>
                </Card.Text>
            </Card.Body>
        </Card>
    );
});

export default StockItemCard;
